import {
  FunnelModeKey,
  IFunnelModeSlice,
  QueryType,
} from '../../../../utils/apollo/graphqlSchema';
import { IAnalysisQueryResponse } from '../../../../utils/apollo/responseTypes';
import { IAliasOption } from '../../../AliasEditor';
import {
  IDisplayedMeasure,
  IParsedChartData,
  IParsedChartDataFunnel,
} from '../../interfaces';
import {
  resetAliasReplacementAction,
  setChartSettingsMeasureVisibility,
  toggleChartSettingsMeasureAction,
  updateAliasReplacementAction,
} from '../../../../redux/slices/analyses';
import { useAppDispatch } from '../../../../redux/hooks';
import { assertCondition } from '../../../../utils/types';
import {
  getMeasureColorFromColorConfig,
  IAnalysisColorConfig,
} from '../../utils/analysisColorConfig';
import React, { ReactElement } from 'react';
import styled from 'styled-components';
import { isLastStepInFreqOrTTCWhichSkipMiddleSteps } from '../../FunnelChart/utils';
import { SvgIcon } from '../../../Icon';
import { theme } from '../../../../styles/theme';
import BadgeRound from '../../../BadgeRound';

interface IProps {
  analysisId: string;
  queryType: QueryType;
  displayedMeasures: IDisplayedMeasure[];
  builderInfo: IAnalysisQueryResponse;
  chartData: IParsedChartData;
  colorConfig: IAnalysisColorConfig;
}

interface IReturn {
  updatedDisplayedMeasures: (IDisplayedMeasure & {
    aliases: IAliasOption[];
    onClickHandler: () => void;
    nameElement: ReactElement;
    color: string;
  })[];
}

const LabelContainer = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: flex;
  column-gap: 4px;
`;

const MeasureName = styled.span`
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const getDisplayedMeasureLabel = (
  measure: IDisplayedMeasure,
  backgroundColor: string,
) => {
  if (measure.isResultKey) {
    // only for special case of Occurrence, where we would have "A+B: Occurrence"
    // for all other result keys, just return the name
    if (measure.label.length > 0) {
      return measure.label + ': ';
    }

    // return empty string, and just render the name
    return '';
  }

  return (
    <BadgeRound size="small" bgColor={backgroundColor}>
      {measure.label}
    </BadgeRound>
  );
};

const isFunnelStepVisible = (
  funnelSlice: IFunnelModeSlice,
  displayedMeasures: IDisplayedMeasure[],
) => {
  const { measureSelection } = funnelSlice;
  assertCondition(measureSelection.length === displayedMeasures.length);
  return measureSelection.every((isSelected, index) => {
    return isSelected === displayedMeasures[index].isVisible;
  });
};

/**
 * This hook is used for funnel.freq and funnel.timeToConvert mode to update
 * steps in the legend.
 * For each step, it will display as relationship: Step1 -> Step2 or Step1 -> Step 4(last).
 * and also provide different event handler for each step like, onClickHandler, aliasOptions to support
 * multi-steps in on measure button.
 */
const useFunnelSteps = ({
  analysisId,
  queryType,
  displayedMeasures,
  builderInfo,
  chartData,
  colorConfig,
}: IProps): IReturn => {
  const dispatch = useAppDispatch();

  // if measure component need to adapt different required based on chart type,
  // then implement useMeasures on interactions.
  const onMeasureClickHandler = (measureIndex: number) => {
    dispatch(toggleChartSettingsMeasureAction({ analysisId, measureIndex }));
  };

  // in freq/ttc, we need to follow the list of selections, for example: [T,T,F,F] to
  // represent step1 -> step2, therefore use a different handler.
  const onSetMeasureVisibility = (newVisibility: boolean[]) => {
    assertCondition(newVisibility.length === displayedMeasures.length);
    dispatch(
      setChartSettingsMeasureVisibility({
        analysisId,
        measuresVisibility: newVisibility,
      }),
    );
  };

  const getUpdatedMeasureAliases = (
    measure: IDisplayedMeasure,
  ): IAliasOption[] => {
    const userAliasId = measure.userAlias.aliasId;
    return [
      {
        type: 'alias',
        analysisId,
        name: measure.name,
        userAliasId,
        apply: (newAlias: string) => {
          dispatch(
            updateAliasReplacementAction({
              analysisId,
              aliasId: userAliasId,
              replacement: newAlias,
            }),
          );
        },
        reset: () => {
          dispatch(
            resetAliasReplacementAction({
              analysisId,
              aliasId: userAliasId,
            }),
          );
        },
      },
    ];
  };

  // main logic to update steps in the legend in funnel.freq and funnel.timeToConvert mode
  if (
    queryType === QueryType.Funnel &&
    builderInfo.__typename === 'FunnelQuery' &&
    (builderInfo?.mode?.key === FunnelModeKey.TimeToConvert ||
      builderInfo?.mode?.key === FunnelModeKey.Frequency)
  ) {
    const { funnelModeSlices } = chartData as IParsedChartDataFunnel;

    const updatedDisplayedMeasures = funnelModeSlices.map((funnelSlice) => {
      const sourceMeasure = displayedMeasures.find(
        (measure) => measure.key === funnelSlice.firstMeasureKey,
      );
      const targetMeasure = displayedMeasures.find(
        (measure) => measure.key === funnelSlice.lastMeasureKey,
      );
      assertCondition(sourceMeasure && targetMeasure);
      // Last step in time to convert : it's a special case that need to determine
      // whether we need to use the color on last step.
      // the only case we use the last color is when all steps are selected and the length >= 3
      const isApplyLastStep = isLastStepInFreqOrTTCWhichSkipMiddleSteps(
        funnelSlice.measureSelection,
      );

      const isVisible = isFunnelStepVisible(funnelSlice, displayedMeasures);
      const sourceMeasureColor = getMeasureColorFromColorConfig(
        colorConfig,
        sourceMeasure.key,
      );
      const targetMeasureColor = getMeasureColorFromColorConfig(
        colorConfig,
        targetMeasure.key,
      );

      const color = isVisible
        ? isApplyLastStep
          ? targetMeasureColor
          : sourceMeasureColor
        : theme.colors.secondary400;

      // need to display Label:name -> Label:name together
      const stepDisplay = (
        <>
          <LabelContainer>
            {getDisplayedMeasureLabel(sourceMeasure, color)}
            <MeasureName>{sourceMeasure.name}</MeasureName>
          </LabelContainer>
          <SvgIcon
            name="arrow-right"
            width={10}
            height={10}
            customFill={color}
          />
          <LabelContainer>
            {getDisplayedMeasureLabel(targetMeasure, color)}
            <MeasureName>{targetMeasure.name}</MeasureName>
          </LabelContainer>
        </>
      );
      return {
        ...(isApplyLastStep ? targetMeasure : sourceMeasure),
        nameElement: stepDisplay,
        color,
        hasUserAlias: sourceMeasure.hasUserAlias || targetMeasure.hasUserAlias,
        isVisible,
        aliases: [
          ...getUpdatedMeasureAliases(sourceMeasure),
          ...getUpdatedMeasureAliases(targetMeasure),
        ],
        onClickHandler: () =>
          onSetMeasureVisibility(funnelSlice.measureSelection),
      };
    });

    return { updatedDisplayedMeasures };
  }

  // regular funnel: conversion mode
  const updatedDisplayedMeasures = displayedMeasures.map((measure, index) => {
    const measureColor = getMeasureColorFromColorConfig(
      colorConfig,
      measure.key,
    );

    const color = measure.isVisible ? measureColor : theme.colors.secondary400;

    return {
      ...measure,
      nameElement: (
        <LabelContainer>
          {getDisplayedMeasureLabel(measure, color)}
          <MeasureName>{measure.name}</MeasureName>
        </LabelContainer>
      ),
      color,
      aliases: getUpdatedMeasureAliases(measure),
      onClickHandler: () => onMeasureClickHandler(index),
    };
  });
  return { updatedDisplayedMeasures };
};

export default useFunnelSteps;
